import { useState, useEffect, useMemo } from 'react';
import {
  Annotorious,
  OpenSeadragonAnnotationPopup,
  OpenSeadragonAnnotator,
  OpenSeadragonViewer,
  AnnotationBody,
  PopupProps,
  W3CImageFormat,
  useAnnotator
} from '@annotorious/react';
import { router } from '@inertiajs/react';
import '@annotorious/react/annotorious-react.css';
import { mountExtension } from '@annotorious/openseadragon-textlayer';
import '@annotorious/openseadragon-textlayer/annotorious-openseadragon-textlayer.css';
import '../styles/annotator.css'

import { NonScrollingInput } from './NonScrollingInput';

export const annotationMapper = (annotation: object) => {
    return {
        ...annotation['attributes'],
        type: "Annotation"
    };
};

const labelValidation = (annotations: object) => {
  if (!annotations.length) {
    return false;
  }

  for (const annotation of annotations) {
    const body = annotation.body.find(body => body.purpose === 'tagging' && body.value);

    if (!body) {
      return false;
    }
  }

  return true;
};

export const findAnnotationLabel = (annotation: object) => {
  const labelBody: AnnotationBody | undefined = annotation.bodies.find(body => body.purpose === 'tagging');
  return labelBody?.value;
};

const GenerateTextLabelOverlays = ({ annotations }: { annotations: Array<object> }) => {
  const anno = useAnnotator();

  useEffect(() => {
    if (!anno) return;

    const textLayer = mountExtension(anno, {
      label: findAnnotationLabel,
      mode: 'fixedScreenSize', // Choose 'fixedPageSize' or 'responsive'
      position: 'bottomleft',
      offsetY: -24
    });

    return () => {
      if (textLayer && typeof textLayer.unmount === 'function') {
        textLayer.unmount();
      }
    };
  }, [anno]);

  return null;
};

const constrainImage = (imageSize, annotation, anno) => {
};

const onSave = (inputRef, props: PopupProps, label: string) => {
  const { annotation, onCreateBody, onUpdateBody, anno } = props;
  const updated = {
    purpose: 'tagging',
    value: label
  };

  const imageSize = anno.viewer.world?.getItemAt(0)?.source?.dimensions || null;
  const labelBody: AnnotationBody | undefined = annotation.bodies.find(body => body.purpose === 'tagging');

  if (imageSize) {
	constrainImage(imageSize, annotation, anno);
  }

  if (labelBody) {
    onUpdateBody(labelBody, updated);
    anno.setUserSelectAction('EDIT');
    anno.setDrawingEnabled(true);
  } else {
    if (!label) {
      anno.removeAnnotation(annotation.id);
    } else {
      onCreateBody(updated);
      anno.setUserSelectAction('EDIT');
      anno.setDrawingEnabled(true);
    }
  }
};

const LabelPopup = (props: PopupProps) => {
  const { annotation, onCreateBody, onUpdateBody } = props;
  const [label, setLabel] = useState('');
  const anno = useAnnotator();

  useEffect(() => {
    const labelBody: AnnotationBody | undefined = annotation.bodies.find(body => body.purpose === 'tagging');
    setLabel(labelBody?.value ?? '');
  }, []);

  return (
    <div>
      <label className="input validator">
        <NonScrollingInput
          className="text-black input-ghost"
          type="text"
          value={label}
          placeholder="Enter rooster name"
          required
          anno={anno}
          annotation={annotation}
          onCreateBody={onCreateBody}
          onUpdateBody={onUpdateBody}
          labelerMethod={setLabel}
          saverMethod={onSave} />
        </label>
    </div>
  );
};

const AnnotationLoader = ({ annotations }: { annotations: Array<object> }) => {
  const formattedAnnotations = annotations.map(annotation => annotationMapper(annotation));
  const anno = useAnnotator();

  useEffect(() => {
    if (anno) {
      anno.setAnnotations(formattedAnnotations ?? [], true);
    }
  }, [anno]);

  return null;
};

const AnnotationSender = ({imageUuid, error}: {imageUuid: string, error: string}) => {
  const anno = useAnnotator();
  const handleAnnotationsSubmit = (e) => {
    e.preventDefault();

    if (!labelValidation(anno.getAnnotations())) {
      error = 'NO_SELECTION';
	  router.visit(`/chickens/annotate/${imageUuid}?error=${error}`, { error: error });
    } else {
      router.post(`/chickens/annotate/${imageUuid}`, { annotations: anno.getAnnotations() }, { onSuccess: (page) => router.visit('/chickens/new', { preserveState: false }) });
    }
  };

  return (
    <>
      <button type="button" onClick={handleAnnotationsSubmit} className="relative z-50 btn block mx-auto mt-2 border-black bg-white hover:bg-gray-200 hover:border-gray-300">Save your rooster(s)</button>
    </>
  );
};

export function Annotator(
    { image, imageUuid, annotations, readOnly, images }:
    { image: string, imageUuid: string | null, annotations: Array<object>, readOnly: boolean | false, images: Array<string> | null }
) {
  const queryParams = new URLSearchParams(location.search);
  const error = queryParams.get('error');
  let sender = (<></>);
  let carousel = (<></>);

  if (!readOnly) {
    sender = (<div><AnnotationSender imageUuid={imageUuid} error={error} /></div>);
  }

  const OSD_OPTS = useMemo(() => ({
    tileSources: {
      type: 'image',
      url: image
    },
    gestureSettingsMouse: {
        scrollToZoom: false,
        clickToZoom: false,
		pinchRotate: false,
        dblClickToZoom: false
    },
    gestureSettingsTouch: {
        pinchToZoom: false,
        scrollToZoom: false,
        clickToZoom: false,
		pinchRotate: false,
        dblClickToZoom: false
    },
    gestureSettingsPen: {
        scrollToZoom: false,
        clickToZoom: false,
        dblClickToZoom: false
    },
    showNavigationControl: false,
    panHorizontal: 	false,
    panVertical: 	false,
	showRotationControl: false,
    defaultZoomLevel: 	0
  }), [image]);

  return (
    <>
      <div className="mx-auto fieldset bg-base-200 border-base-300 rounded-md max-w-3lg border p-4 bg-gray-200">
        <Annotorious>
          <style>{`.openseadragon-container { min-height: 400px !important; }`}</style>
          <div className="bg-white min-h-[300px] max-w-3lg rounded-md">
            <OpenSeadragonAnnotator adapter={W3CImageFormat("rooster-vision")} drawingEnabled={readOnly ? false:true} drawingMode="drag" userSelectAction={readOnly ? 'NONE':'EDIT'}>
              <OpenSeadragonViewer options={OSD_OPTS} className="rounded-md min-h-[300px]!" />
              <OpenSeadragonAnnotationPopup popup={(props: PopupProps) => (
                <LabelPopup {...props} />
              )} />
              <GenerateTextLabelOverlays annotations={annotations} />
            </OpenSeadragonAnnotator>
            <AnnotationLoader annotations={annotations} />
          </div>
          {sender}
        </Annotorious>
      </div>
      {carousel}
    </>
  );
}
